"use client";

import { Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  listPipelines,
  submitJob,
  uploadImage,
  warmup,
} from "../lib/api";
import type { Job, PipelineManifest } from "../lib/types";
import { JobProgress } from "./job-progress";
import { ModelViewer } from "./model-viewer";
import { PipelineStatus } from "./pipeline-status";
import { useServiceHealth } from "../lib/use-service-health";

export function GenerateForm() {
  const [pipelines, setPipelines] = useState<PipelineManifest[]>([]);
  const [pipelineId, setPipelineId] = useState<string>("sf3d");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeJob, setActiveJob] = useState<Job | null>(null);
  const [completedJobId, setCompletedJobId] = useState<string | null>(null);
  const [pendingForReady, setPendingForReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { health, isReady } = useServiceHealth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastPreviewRef = useRef<string | null>(null);

  // Warm both models on mount; SSE health surfaces load progress.
  useEffect(() => {
    void warmup(["sf3d"]).catch((e) => {
      console.warn("[warmup] failed", e);
    });
  }, []);

  // Pull pipeline manifests once.
  useEffect(() => {
    listPipelines()
      .then((m) => {
        setPipelines(m);
        const first = m[0];
        if (first && !m.some((p) => p.id === pipelineId)) {
          setPipelineId(first.id);
        }
      })
      .catch((e: unknown) => setError((e as Error).message));
  }, [pipelineId]);

  // Auto-replay when the model the user picked finishes loading.
  useEffect(() => {
    if (!pendingForReady) return;
    if (isReady(pipelineId) && imageFile) {
      setPendingForReady(false);
      void doSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingForReady, health, pipelineId]);

  const onFile = (file: File | null) => {
    setImageFile(file);
    if (lastPreviewRef.current) {
      URL.revokeObjectURL(lastPreviewRef.current);
      lastPreviewRef.current = null;
    }
    if (file) {
      const url = URL.createObjectURL(file);
      lastPreviewRef.current = url;
      setPreviewUrl(url);
    } else {
      setPreviewUrl(null);
    }
  };

  const doSubmit = async () => {
    if (!imageFile) return;
    setError(null);
    setSubmitting(true);
    try {
      const upload = await uploadImage(imageFile);
      const job = await submitJob({
        pipelineId,
        imageKeys: [upload.key],
      });
      setActiveJob(job);
      setCompletedJobId(null);
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!imageFile) {
      setError("Pick an image first");
      return;
    }
    if (!isReady(pipelineId)) {
      // Model not loaded — queue the request and let the SSE health stream
      // trigger the auto-replay above.
      setPendingForReady(true);
      void warmup([pipelineId]).catch(() => {});
      return;
    }
    void doSubmit();
  };

  const selectedPipeline = pipelines.find((p) => p.id === pipelineId);
  const ready = isReady(pipelineId);

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-4 py-6">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold">3D Generation</h1>
        <PipelineStatus health={health} pipelines={pipelines} />
      </header>

      <form onSubmit={onSubmit} className="space-y-4 rounded-lg border p-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Pipeline</label>
          <select
            value={pipelineId}
            onChange={(e) => setPipelineId(e.target.value)}
            className="w-full rounded border bg-background px-3 py-2 text-sm"
            disabled={submitting}
          >
            {pipelines.map((p) => (
              <option key={p.id} value={p.id}>
                {p.displayName} {p.estimatedTimeMs ? `(~${Math.round(p.estimatedTimeMs / 1000)}s)` : ""}
              </option>
            ))}
            {pipelines.length === 0 && <option>Loading…</option>}
          </select>
          {selectedPipeline && (
            <p className="text-xs text-muted-foreground">{selectedPipeline.description}</p>
          )}
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Source image</label>
          <div
            className="flex aspect-square w-full max-w-xs cursor-pointer items-center justify-center overflow-hidden rounded border border-dashed text-sm text-muted-foreground"
            onClick={() => fileInputRef.current?.click()}
          >
            {previewUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={previewUrl} alt="preview" className="h-full w-full object-contain" />
            ) : (
              <span className="flex flex-col items-center gap-2">
                <Upload className="h-6 w-6" />
                Click to choose image
              </span>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => onFile(e.target.files?.[0] ?? null)}
          />
        </div>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            className="rounded bg-foreground px-4 py-2 text-sm font-medium text-background disabled:opacity-50"
            disabled={submitting || !imageFile}
          >
            {pendingForReady ? "Waiting for model…" : ready ? "Generate" : "Generate (loading model)"}
          </button>
          {error && <span className="text-xs text-red-500">{error}</span>}
        </div>
      </form>

      {activeJob && (
        <section className="space-y-2 rounded-lg border p-4">
          <h2 className="text-sm font-medium">Job {activeJob.id.slice(0, 8)}</h2>
          <JobProgress
            jobId={activeJob.id}
            onCompleted={() => setCompletedJobId(activeJob.id)}
            onFailed={(msg) => setError(msg)}
          />
        </section>
      )}

      {completedJobId && (
        <section className="rounded-lg border p-4">
          <h2 className="mb-3 text-sm font-medium">Result</h2>
          <div className="aspect-square w-full overflow-hidden rounded bg-zinc-900">
            <ModelViewer jobId={completedJobId} />
          </div>
        </section>
      )}
    </div>
  );
}
